/**
 * Anti-slop rules for AI-generated content quality enforcement.
 * Used in prompts (embedded as instructions) and in Stage 5 validation (regex checks).
 */

/**
 * Banned copy phrases — AI-sounding filler words and clichés
 */
export const BANNED_COPY_PHRASES = [
  'elevate',
  'leverage',
  'unlock',
  'seamless',
  'cutting-edge',
  'game-changer',
  'best-in-class',
  'world-class',
  'next-level',
  'take it to the next level',
  'harness the power',
  'revolutionize',
  'supercharge',
  'empower',
  'synergy',
  'paradigm',
  'disrupt',
  'holistic',
  'robust solution',
  'turnkey',
  'scalable solution',
  'end-to-end',
  'state-of-the-art',
] as const;

/**
 * Banned opening phrases — dead giveaways of AI-written copy
 */
export const BANNED_OPENING_PHRASES = [
  "in today's fast-paced",
  "in today's digital",
  "in today's competitive",
  'look no further',
  'are you tired of',
  'imagine a world where',
  'picture this',
  'say goodbye to',
  'introducing the future of',
] as const;

/**
 * Banned pattern phrases with placeholders
 */
export const BANNED_PATTERN_PHRASES = [
  'as a [type], you know',
  'whether you are a',
  'from [x] to [y]',
] as const;

/**
 * Generic headlines that should be replaced with specific benefit-driven ones
 */
export const GENERIC_HEADLINES = [
  'our features',
  'why choose us',
  'about us',
  'welcome',
  'get started',
  'our services',
  'what we offer',
  'our solutions',
  'how it works',
  'testimonials',
  'our team',
  'contact us',
  'features',
  'benefits',
  'pricing',
] as const;

/**
 * Generic CTA text that should be replaced with action-specific text
 */
export const GENERIC_CTAS = [
  'submit',
  'click here',
  'learn more',
  'read more',
  'find out more',
] as const;

/**
 * Overused Tailwind/default colors to avoid
 */
export const BANNED_COLORS = [
  '#4F46E5', // indigo-600
  '#3B82F6', // blue-500
  '#6366F1', // indigo-500
  '#8B5CF6', // violet-500
  '#EF4444', // red-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#6B7280', // gray-500
  '#1F2937', // gray-800 (overused as text)
  '#F3F4F6', // gray-100 (overused as bg)
  '#F9FAFB', // gray-50
] as const;

/**
 * Font pairings to avoid (same font for heading + body)
 */
export const BANNED_FONT_PAIRINGS = [
  ['Inter', 'Inter'],
  ['Roboto', 'Roboto'],
  ['Open Sans', 'Open Sans'],
] as const;

/**
 * Spacing scale for consistency checks
 */
export const SPACING_SCALE = [0, 5, 10, 15, 20, 30, 40, 60, 80, 100, 120] as const;

/**
 * Format anti-slop copy rules as prompt instructions
 */
export const getCopyRulesForPrompt = (): string => {
  return `## Copy Quality Rules

BANNED PHRASES (never use these):
${BANNED_COPY_PHRASES.map((p) => `- "${p}"`).join('\n')}

BANNED OPENINGS (never start sentences with):
${BANNED_OPENING_PHRASES.map((p) => `- "${p}"`).join('\n')}

HEADLINE RULES:
- Must state a specific benefit, not a generic category
- 3-8 words for hero headline
- Never use generic section headers like: ${GENERIC_HEADLINES.slice(0, 8)
    .map((h) => `"${h}"`)
    .join(', ')}
- Instead: describe what the reader gains or learns

CTA RULES:
- Must be action-specific: "Start Free Trial", "Get Your Quote", "Book a Demo"
- Never use: ${GENERIC_CTAS.map((c) => `"${c}"`).join(', ')}

BODY COPY RULES:
- Every sentence must convey specific information
- Use concrete numbers and details when possible
- No filler sentences that could be removed without losing meaning
- Write like a skilled copywriter, not a corporate template`;
};

/**
 * Format anti-slop design rules as prompt instructions
 */
export const getDesignRulesForPrompt = (): string => {
  return `## Design Quality Rules

COLOR RULES:
- Do NOT use these overused default colors: ${BANNED_COLORS.slice(0, 6).join(', ')}
- Background should not be pure white (#FFFFFF) — use a slight warm or cool tint
- Ensure WCAG AA contrast (4.5:1) for all text on backgrounds
- Use the global style named colors (primary, secondary, etc.) for brand consistency
- Use explicit hex/rgba for section backgrounds and decorative variety

SPACING RULES:
- Use values from this scale: ${SPACING_SCALE.join(', ')}px
- Sections should have generous padding (40-120px vertical)
- Mobile padding should be reduced (60-70% of desktop)

TYPE HIERARCHY:
- One h1 per page (hero section only)
- h2 for section titles
- h3 for sub-elements within sections
- Never skip heading levels

LAYOUT VARIETY:
- No two consecutive sections should use the same layout pattern
- Include at least one dark/reversed section (dark bg + light text)
- Alternate between full-width, contained, and asymmetric layouts

SHAPE DIVIDERS:
- Use shapeDivider on sections that have a background image (bgImage) to create a smooth visual transition at the section edge
- Do NOT set shapeDividerColor — it auto-resolves to the neighboring section's background`;
};

/**
 * Format color ban rules for the style generation prompt
 */
export const getColorBanRulesForPrompt = (): string => {
  return `BANNED COLORS — Do not use any of these overused defaults as primary, secondary, or accent colors:
${BANNED_COLORS.map((c) => `- ${c}`).join('\n')}

Instead, choose distinctive colors that reflect the brand's personality.
A coffee shop should NOT look like a tech startup. A law firm should NOT look like a children's app.

FONT PAIRING RULES:
- Heading and body fonts MUST be different families
- Choose fonts that create visual contrast (serif + sans-serif, geometric + humanist, etc.)
- Banned same-font pairings: ${BANNED_FONT_PAIRINGS.map(([a, b]) => `${a}+${b}`).join(', ')}`;
};
